import { Autocomplete, CircularProgress, TextField } from "@mui/material";

import { Device } from "@/shared/domain/devices";
import { useAllDevicesByOrg } from "@/shared/domain/devices/hooks/useAllDevicesByOrg";

import styles from "./DeviceSelector.module.css";

interface DeviceSelectorProps {
  /**
   * Selected device id — also the literal text shown in the field. The
   * input is fully controlled by this prop: every value passed to
   * `setDeviceId` (including each keystroke of free-typed text) must be
   * written straight back into `deviceId`, or typing won't render. A plain
   * `useState` setter satisfies this; a parent that debounces or
   * validates-and-rejects updates will freeze the field's text.
   */
  deviceId: string | null;
  /** See `deviceId` for the synchronous write-back requirement. */
  setDeviceId: (deviceId: string | null) => void;
  orgId: string | undefined;
  disabled?: boolean;
  size?: "small" | "medium";
  placeholder?: string;
}

export function DeviceSelector({
  deviceId,
  setDeviceId,
  orgId,
  disabled = false,
  size = "small",
  placeholder = "Select device",
}: DeviceSelectorProps) {
  const { data: devices, isLoading } = useAllDevicesByOrg(orgId);

  const options = devices ?? [];

  return (
    <Autocomplete
      id="device-selector"
      options={options}
      fullWidth
      freeSolo
      value={deviceId}
      // Controlled so the field empties when the parent resets deviceId;
      // freeSolo otherwise retains the stale typed text.
      inputValue={deviceId ?? ""}
      size={size}
      disabled={disabled}
      loading={isLoading}
      getOptionLabel={(option) => {
        if (typeof option === "string") {
          return option;
        }
        return option.device_id;
      }}
      isOptionEqualToValue={(option, value) => {
        if (typeof option === "string" && typeof value === "string") {
          return option === value;
        }
        if (typeof option === "object" && typeof value === "string") {
          return option.device_id === value;
        }
        if (typeof option === "object" && typeof value === "object") {
          return option.device_id === value.device_id;
        }
        return false;
      }}
      renderOption={(props, option: Device | string) => (
        <li className={styles.optionItem} {...props}>
          <span className={styles.optionText}>
            {typeof option === "string" ? option : option.device_id}
          </span>
        </li>
      )}
      onChange={(_event, newValue: Device | string | null) => {
        if (newValue === null) {
          setDeviceId(null);
        } else if (typeof newValue === "object") {
          setDeviceId(newValue.device_id);
        } else {
          setDeviceId(newValue);
        }
      }}
      onInputChange={(_event, newInputValue, reason) => {
        // inputValue is controlled by deviceId, so user-entered text must
        // write back or keystrokes won't render. "reset" and "clear" changes
        // already originate from deviceId.
        if (reason === "input") {
          setDeviceId(newInputValue || null);
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? (
                  <CircularProgress color="inherit" size={16} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      componentsProps={{
        paper: {
          className: styles.dropdownPaper,
        },
      }}
      className={styles.autocomplete}
    />
  );
}
